import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Input from "../../../components/input";
import { useTranslate } from "../../../context/TranslationContext";
import {
  getBFPChartData,
  getBFPStats,
  getBMIChartData,
  getBMIStats,
  getBMRChartData,
  getBMRStats,
  getHRCChartData,
  getHydrationChartData,
  getHydrationStats,
  getIBWChartData,
  getIBWStats,
  getMacroChartData,
  getMacroStats,
  getORMChartData,
  getORMStats,
  getWHRChartData,
  getWHRStats,
} from "../../../screens/calculators/graph";
import {
  getBalanceChartData,
  getBalanceStats,
  getCardioFitnessChartData,
  getCardioStats,
  getFlexibilitySitChartData,
  getFlexibilityStats,
  getStrengthChartData,
  getStrengthStats,
} from "../../../screens/fitnesstest/graph";
import { useStaticData } from "../../../utils/data";
import {
  formatToMonthDay,
  getLastNDaysLabels,
  getZoneColor,
} from "../../../utils/helper";
import Tabs from "../../tabs";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  BarElement,
  Filler
);

const FitnessToolsResultCard = ({
  type,
  result,
  historyData = [],
  showHistory = false,
  toggleHistory,
  handleSaveResult,
  handleDeleteLast,
  formatHistoryEntry,
  formatDateTime,
  chartData = [],
  period,
  handlePeriodChange,
  selectedStrength,
  handleStrengthTabChange,
  selectedFlexibility,
  handleFlexibilityTabChange,
  selectedBalence,
  handleBalenceTabChange,
}) => {
  const { t } = useTranslate("toolsResultCard");
  const {
    baseBMIThresholds,
    baseThresholds,
    baseThresholdsShoulder,
    CALCULATOR_DATA,
  } = useStaticData();
  const { user } = useSelector((state) => state.authSlice);
  const gender = user?.user_obj?.gender?.toLowerCase();
  const [showCalculated, setShowCalculated] = useState(false);
  const { min, max, stepSize } =
    type === "BMR"
      ? getBMRStats(Array.isArray(chartData) ? chartData : [])
      : {};

  const { ormMin, ormMax, ormStepSize } =
    type === "ORM"
      ? getORMStats(Array.isArray(chartData) ? chartData : [])
      : {};

  const { cardioMin, cardioMax, cardioStepSize } =
    type === "Cardio"
      ? getCardioStats(Array.isArray(chartData) ? chartData : [])
      : {};

  const { whrMin, whrMax, whrStepSize } =
    type === "WHR"
      ? getWHRStats(Array.isArray(chartData) ? chartData : [], gender)
      : {};

  const { strengthMin, strengthMax, strengthStepSize } =
    type === "Strength"
      ? getStrengthStats(Array.isArray(chartData) ? chartData : [])
      : {};

  const { flexibilityMin, flexibilityMax, flexibilityStepSize } =
    type === "Flexibility"
      ? getFlexibilityStats(Array.isArray(chartData) ? chartData : [])
      : {};

  const { bmiMin, bmiMax, bmiStepSize } =
    type === "BMI"
      ? getBMIStats(Array.isArray(chartData) ? chartData : [])
      : {};

  const { bfpMin, bfpMax, bfpStepSize } =
    type === "Body Fat" || type === "BodyFat"
      ? getBFPStats(Array.isArray(chartData) ? chartData : [])
      : {};

  const { macroMin, macroMax, macroStepSize } =
    type === "Macro"
      ? getMacroStats(Array.isArray(chartData) ? chartData : [])
      : {};

  const { hydMin, hydMax, hydStepSize } =
    type === "Hydration"
      ? getHydrationStats(Array.isArray(chartData) ? chartData : [])
      : {};

  const { balMin, balMax, balStepSize } =
    type === "Balance"
      ? getBalanceStats(
          Array.isArray(chartData) ? chartData : [],
          selectedBalence
        )
      : {};

  const { ibwMin, ibwMax, ibwStepSize } =
    type === "IBW"
      ? getIBWStats(Array.isArray(chartData) ? chartData : [])
      : {};

  const STATUS_KEYS = ["poor", "lightRecovery", "balanced", "deepRecovery"];
  const statuses = STATUS_KEYS.map((key) => t(key));

  const apiStatus = chartData?.status || "";

  const activeIndex = STATUS_KEYS.findIndex(
    (key, i) =>
      key.toLowerCase() === apiStatus.toLowerCase() ||
      statuses[i].toLowerCase() === apiStatus.toLowerCase()
  );

  const toolDescriptions = {
    BMI: t("description.bmi"),
    "Body Mass Index (BMI)": t("description.bmi"),
    BMR: t("description.bmr"),
    BodyFat: t("description.bodyFat"),
    "Body Fat": t("description.bodyFat"),
    Macro: t("description.macro"),
    HRC: t("description.hrc"),
    IBW: t("description.ibw"),
    Hydration: t("description.hydration"),
    ORM: t("description.orm"),
    Sleep: t("description.sleep"),
    WHR: t("description.whr"),
    Balance: t("description.balance"),
    Cardio: t("description.cardio"),
    Strength: t("description.strength"),
    Flexibility: t("description.flexibility"),
  };

  const getResultDisplay = () => {
    if (!result && (!historyData || historyData.length === 0)) return "XXXX";

    const sortedHistory = [...historyData].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
    const latestHistory = sortedHistory[0];

    if (result) {
      switch (type) {
        case "BMI":
        case "Body Mass Index (BMI)":
          return `${result.bmi} kcal/day`;
        case "BMR":
          return `${result.bmr} kcal`;
        case "BodyFat":
        case "Body Fat":
          return `${result.bodyFat} %`;
        case "Macro":
          return (
            <>
              TDEE: {result.macros?.tdee || result.tdee} kcal
              <br />
              {t("protein")}: {result.macros?.protein} g<br />
              {t("carbs")}: {result.macros?.carbs} g<br />
              {t("fat")}: {result.macros?.fat} g
            </>
          );
        case "HRC":
          return `${result.targetHR} bpm`;
        case "IBW":
          return `${result.result} kg`;
        case "Hydration":
          return `${result.liters} L (${result.cups} cups)`;
        case "ORM":
          return `${result.result} kg`;
        case "Sleep":
          return `${result.result} hours`;
        case "WHR":
          return `${result.result} (${result.classification || "N/A"})`;
        case "Balance":
          return `${result.result}`;
        case "Cardio":
          return `${result.result} ml/kg/min`;
        case "Flexibility":
          return `${result.result}`;
        case "Strength":
          return `${result.result}`;
        default:
          return result[type.toLowerCase()] || result.result || "XXXX";
      }
    } else if (latestHistory?.result) {
      switch (type) {
        case "BMI":
        case "Body Mass Index (BMI)":
          return `${latestHistory.result} kcal/day`;
        case "BMR":
          return `${latestHistory.result} kcal`;
        case "BodyFat":
        case "Body Fat":
          return `${latestHistory.result} %`;
        case "HRC":
          return `${latestHistory.result} bpm`;
        case "IBW":
          return `${latestHistory.result} kg`;
        case "Hydration":
          return `${latestHistory.result}`;
        case "ORM":
          return `${latestHistory.result} kg`;
        case "Sleep":
          return `${latestHistory.result} hours`;
        case "WHR":
          return `${latestHistory.result}`;
        case "Macro":
          return (
            <>
              TDEE: {latestHistory.result?.tdee} kcal
              <br />
              {t("protein")}: {latestHistory.result?.protein} g<br />
              {t("carbs")}: {latestHistory.result?.carbs} g<br />
              {t("fat")}: {latestHistory.result?.fat} g
            </>
          );
        case "Balance":
          return `${latestHistory.result}`;
        case "Cardio":
          return `${latestHistory.result} ml/kg/min`;
        case "Flexibility":
          return `${latestHistory.result}`;
        case "Strength":
          return `${latestHistory.result}`;
        default:
          return latestHistory.result || "XXXX";
      }
    }

    return "XXXX";
  };

  useEffect(() => {
    if (result) {
      setShowCalculated(true);
    } else {
      setShowCalculated(false);
    }
  }, [result]);

  return (
    <div className="col-12 my-3 d-flex">
      <div
        className="w-100 mt-4 p-3 p-md-5"
        style={{
          backgroundColor: "#161617",
          borderRadius: "2rem",
          color: "#fff",
        }}
      >
        <h4
          className="text-uppercase text-center mb-2"
          style={{ fontSize: "2rem", fontWeight: "bold" }}
        >
          {type}
        </h4>
        <h3
          className="text-white text-center mb-2"
          style={{ fontSize: "1.5rem" }}
        >
          {getResultDisplay()}
        </h3>
        <p
          className="m-0 text-white text-center"
          style={{ fontSize: "1rem", lineHeight: "1.5" }}
        >
          {toolDescriptions[type] || "This is the result of your calculation."}
        </p>
        <div className="d-flex flex-column flex-md-row gap-2 mt-3 justify-content-center">
          <button
            className="site-btn"
            style={{
              minWidth: "120px",
              fontSize: "0.875rem",
              padding: "8px 16px",
            }}
            onClick={() => {
              setShowCalculated(true);
              if (showHistory) toggleHistory();
            }}
          >
            <i className="fa-solid fa-eye me-2" />
            {t("viewResult")}
          </button>
          <button
            className="site-btn"
            style={{
              minWidth: "120px",
              fontSize: "0.875rem",
              padding: "8px 16px",
            }}
            onClick={() =>
              result
                ? handleSaveResult(type, result)
                : toast.warn(
                    `${t("pleaseCalc")} ${
                      type === "Body Fat" ? "Body Fat Percentage" : type
                    } ${t("beforeSave")}`
                  )
            }
          >
            <i className="fa-solid fa-save me-2" />
            {t("saveProgress")}
          </button>
          <button
            className="site-btn"
            style={{
              minWidth: "120px",
              fontSize: "0.875rem",
              padding: "8px 16px",
            }}
            onClick={() => handleDeleteLast(type)}
          >
            <i className="fa-solid fa-trash me-2" />
            {t("deleteLast")}
          </button>
          <button
            className="site-btn"
            style={{
              minWidth: "120px",
              fontSize: "0.875rem",
              padding: "8px 16px",
            }}
            onClick={() => {
              toggleHistory(type);
              if (showCalculated) setShowCalculated(false);
            }}
          >
            <i className="fa-solid fa-clock-rotate-left me-2" />
            {t("viewHistory")}
          </button>
        </div>
        <div className="mt-3">
          {showHistory ? (
            <>
              <p
                className="m-0 text-white mb-2 fw-bold"
                style={{ fontSize: "1rem" }}
              >
                {t("resultHistory")}
              </p>
              {historyData.length > 0 ? (
                historyData.map((entry, index) => (
                  <div
                    key={index}
                    className="m-0 text-white"
                    style={{ fontSize: "0.875rem" }}
                  >
                    {formatHistoryEntry(entry, type)}
                  </div>
                ))
              ) : (
                <p className="m-0 text-white" style={{ fontSize: "0.875rem" }}>
                  {t("noHistory")}
                </p>
              )}
            </>
          ) : showCalculated ? (
            <div>
              <p
                className="m-0 text-white mb-2 fw-bold"
                style={{ fontSize: "1rem" }}
              >
                {t("calculated")}
              </p>
              <div className="d-flex flex-column flex-md-row justify-content-between">
                <p
                  className="flex-grow-1 m-0 text-white"
                  style={{ fontSize: "0.875rem" }}
                >
                  {result ? formatDateTime(result.timestamp) : t("notCalc")}
                </p>
                <p
                  className="m-0 fw-bold text-white"
                  style={{ fontSize: "0.875rem" }}
                >
                  {(() => {
                    if (!result) return "";
                    switch (type) {
                      case "BMI":
                      case "Body Mass Index (BMI)":
                        return `${result.bmi} kcal/day`;
                      case "BodyFat":
                      case "Body Fat":
                        return `${result.bodyFat} %`;
                      case "BMR":
                        return `${result.bmr} kcal`;
                      case "HRC":
                        return `${result.targetHR} bpm`;
                      case "ORM":
                        return `${result.result} kg`;
                      case "IBW":
                        return `${result.result} kg`;
                      case "Hydration":
                        return `${result.liters} L (${result.cups} cups)`;
                      case "Sleep":
                        return `${result.result} hours`;
                      case "WHR":
                        return `${result.result} (${
                          result.classification || "N/A"
                        })`;
                      case "Macro":
                        return (
                          <>
                            TDEE: {result.macros?.tdee || result.tdee} kcal
                            <br />
                            {t("protein")}: {result.macros?.protein} g<br />
                            {t("carbs")}: {result.macros?.carbs} g<br />
                            {t("fat")}: {result.macros?.fat} g
                          </>
                        );
                      case "Balance":
                        return `${result.result}`;
                      case "Cardio":
                        return `${result.result} ml/kg/min`;
                      case "Flexibility":
                        return `${result.result}`;
                      case "Strength":
                        return `${result.result}`;
                      default:
                        return (
                          result[type.toLowerCase()] || result.result || "N/A"
                        );
                    }
                  })()}
                </p>
              </div>
            </div>
          ) : null}
        </div>
        <div className="mt-4">
          {type === "Cardio" && (
            <>
              <h5 className="text-white text-center flex-grow-1">
                CARDIO FITNESS PROGRESS
              </h5>
              <div className="d-flex justify-content-between align-items-center">
                <div
                  className="ms-auto"
                  style={{ height: "100px", width: "200px" }}
                >
                  <Input
                    type="select"
                    options={CALCULATOR_DATA.CHARTDROPDOWN}
                    value={period}
                    onChange={handlePeriodChange}
                  />
                </div>
              </div>
              <div style={{ height: "300px", width: "100%" }}>
                {chartData?.length > 0 ? (
                  <Line
                    data={getCardioFitnessChartData(chartData, period)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          min: cardioMin,
                          max: cardioMax,
                          ticks: {
                            color: "#ccc",
                            stepSize: cardioStepSize,
                            callback: (value) => `${value}`,
                          },
                          grid: { color: "#333" },
                        },
                        x: {
                          ticks: {
                            color: "#ccc",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") {
                                return label;
                              }

                              if (label === firstLabel || label === lastLabel) {
                                return label;
                              }

                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: {
                          labels: {
                            filter: function (legendItem, chartData) {
                              return legendItem.text !== "Cardio Fitness";
                            },
                            color: "#fff",
                            usePointStyle: true,
                            boxWidth: 10,
                            padding: 15,
                          },
                          position: "top",
                        },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            title: () => [],
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) return t("notData");
                              return `${context.label}  ${value} mL./kg/m`;
                            },
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <Line
                    data={getCardioFitnessChartData(chartData, period)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          min: cardioMin,
                          max: cardioMax,
                          ticks: {
                            color: "#ccc",
                            stepSize: cardioStepSize,
                            callback: (value) => `${value}`,
                          },
                          grid: { color: "#333" },
                        },
                        x: {
                          ticks: {
                            color: "#ccc",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") {
                                return label;
                              }

                              if (label === firstLabel || label === lastLabel) {
                                return label;
                              }

                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: {
                          labels: {
                            filter: function (legendItem, chartData) {
                              return legendItem.text !== "Cardio Fitness";
                            },
                            color: "#fff",
                            usePointStyle: true,
                            boxWidth: 10,
                            padding: 15,
                          },
                          position: "top",
                        },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            title: () => [],
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) return t("notData");
                              return `${context.label}  ${value} mL./kg/m`;
                            },
                          },
                        },
                      },
                    }}
                  />
                )}
              </div>
            </>
          )}

          {type === "Flexibility" && (
            <>
              <h5 className="text-white text-center">FLEXIBILITY PROGRESS</h5>
              <div className="d-flex justify-content-between align-items-center">
                <div
                  className="ms-auto"
                  style={{ height: "100px", width: "200px" }}
                >
                  <Input
                    type="select"
                    options={CALCULATOR_DATA.CHARTDROPDOWN}
                    value={period}
                    onChange={handlePeriodChange}
                  />
                </div>
              </div>
              <Tabs
                tabs={CALCULATOR_DATA.FLEXIBILITY.map((option) => ({
                  label: <>{option.label}</>,
                  value: option.value,
                }))}
                active={CALCULATOR_DATA.FLEXIBILITY.findIndex(
                  (opt) => opt.value === selectedFlexibility
                )}
                setActive={handleFlexibilityTabChange}
              />
              <div style={{ height: "300px", width: "100%" }}>
                {selectedFlexibility === "sit" && (
                  <>
                    {chartData?.length > 0 ? (
                      <Line
                        data={getFlexibilitySitChartData(
                          chartData,
                          period,
                          gender
                        )}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              min: flexibilityMin,
                              max: flexibilityMax,
                              ticks: {
                                stepSize: flexibilityStepSize,
                                color: "#ccc",
                                callback: (value) => `${value}`,
                              },
                            },
                            x: {
                              ticks: {
                                color: "#ccc",
                                autoSkip: period === "week",
                                maxRotation: 0,
                                minRotation: 0,
                                callback: function (value, index) {
                                  const label = this.getLabelForValue(value);
                                  const fullLabels = this.chart.data.labels;
                                  const firstLabel = fullLabels[0];
                                  const lastLabel =
                                    fullLabels[fullLabels.length - 1];

                                  if (period === "week") return label;
                                  if (
                                    label === firstLabel ||
                                    label === lastLabel
                                  )
                                    return label;
                                  return index % 5 === 0 ? label : "";
                                },
                              },
                              grid: { display: false },
                            },
                          },
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              backgroundColor: "#1c1c1e",
                              titleColor: "#fff",
                              bodyColor: "#ccc",
                              callbacks: {
                                title: (tooltipItems) =>
                                  `${tooltipItems[0].label}`,
                                label: (context) => {
                                  const value = context.parsed.y;
                                  if (value === 0) return t("notData");

                                  const thresholds =
                                    baseThresholds[gender] || [];
                                  const zone = thresholds
                                    .slice()
                                    .reverse()
                                    .find((t) => value >= t.min);

                                  return `${value} cm — ${zone?.label || ""}`;
                                },
                              },
                            },
                          },
                        }}
                        plugins={[
                          {
                            id: "zoneLabelPlugin",
                            beforeDatasetsDraw(chart) {
                              const {
                                ctx,
                                chartArea: { top, bottom, left, right },
                                scales: { y },
                              } = chart;
                              if (!y) return;

                              const thresholds = baseThresholds[gender] || [];

                              thresholds.forEach((zone) => {
                                const from = Math.max(zone.min, y.min);
                                const to = Math.min(
                                  zone.max === Infinity ? y.max : zone.max,
                                  y.max
                                );

                                const yFrom = y.getPixelForValue(from);
                                const yTo = y.getPixelForValue(to);
                                const midY = (yFrom + yTo) / 2;

                                ctx.save();
                                ctx.fillStyle =
                                  getZoneColor(zone.label) || "#99999933";
                                ctx.fillRect(
                                  left,
                                  yTo,
                                  right - left,
                                  yFrom - yTo
                                );
                                ctx.restore();

                                ctx.save();
                                ctx.strokeStyle = "#00000033";
                                ctx.beginPath();
                                ctx.moveTo(left, yTo);
                                ctx.lineTo(right, yTo);
                                ctx.stroke();
                                ctx.restore();

                                ctx.save();
                                ctx.fillStyle = "#fff";
                                ctx.font = "bold 13px sans-serif";
                                ctx.textAlign = "center";
                                ctx.textBaseline = "middle";
                                ctx.fillText(
                                  zone.label,
                                  (left + right) / 2,
                                  midY
                                );
                                ctx.restore();
                              });
                            },
                          },
                        ]}
                      />
                    ) : (
                      <Line
                        data={getFlexibilitySitChartData(
                          chartData,
                          period,
                          gender
                        )}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              min: flexibilityMin || 0,
                              max: flexibilityMax || 100,
                              ticks: {
                                stepSize: flexibilityStepSize || 10,
                                color: "#ccc",
                                callback: (value) => `${value}`,
                              },
                              grid: { color: "#444" },
                            },
                            x: {
                              ticks: {
                                color: "#ccc",
                                autoSkip: period === "week",
                                maxRotation: 0,
                                minRotation: 0,
                                callback: function (value, index) {
                                  const label = this.getLabelForValue(value);
                                  const fullLabels = this.chart.data.labels;
                                  const firstLabel = fullLabels[0];
                                  const lastLabel =
                                    fullLabels[fullLabels.length - 1];

                                  if (period === "week") return label;
                                  if (
                                    label === firstLabel ||
                                    label === lastLabel
                                  )
                                    return label;
                                  return index % 5 === 0 ? label : "";
                                },
                              },
                              grid: { display: false },
                            },
                          },
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              backgroundColor: "#1c1c1e",
                              titleColor: "#fff",
                              bodyColor: "#ccc",
                              callbacks: {
                                title: (tooltipItems) =>
                                  `${tooltipItems[0].label}`,
                                label: (context) => {
                                  const value = context.parsed.y;
                                  if (value === 0) return t("notData");

                                  const thresholds =
                                    baseThresholds[gender] || [];
                                  const zone = thresholds
                                    .slice()
                                    .reverse()
                                    .find((t) => value >= t.min);

                                  return `${value} cm — ${zone?.label || ""}`;
                                },
                              },
                            },
                          },
                        }}
                        plugins={[
                          {
                            id: "zoneLabelPlugin",
                            beforeDatasetsDraw(chart) {
                              const {
                                ctx,
                                chartArea: { top, bottom, left, right },
                                scales: { y },
                              } = chart;
                              if (!y) return;

                              const thresholds = baseThresholds[gender] || [];

                              thresholds.forEach((zone) => {
                                const from = Math.max(zone.min, y.min);
                                const to = Math.min(
                                  zone.max === Infinity ? y.max : zone.max,
                                  y.max
                                );

                                const yFrom = y.getPixelForValue(from);
                                const yTo = y.getPixelForValue(to);
                                const midY = (yFrom + yTo) / 2;

                                ctx.save();
                                ctx.fillStyle =
                                  getZoneColor(zone.label) || "#99999933";
                                ctx.fillRect(
                                  left,
                                  yTo,
                                  right - left,
                                  yFrom - yTo
                                );
                                ctx.restore();

                                ctx.save();
                                ctx.strokeStyle = "#00000033";
                                ctx.beginPath();
                                ctx.moveTo(left, yTo);
                                ctx.lineTo(right, yTo);
                                ctx.stroke();
                                ctx.restore();

                                ctx.save();
                                ctx.fillStyle = "#fff";
                                ctx.font = "bold 13px sans-serif";
                                ctx.textAlign = "center";
                                ctx.textBaseline = "middle";
                                ctx.fillText(
                                  zone.label,
                                  (left + right) / 2,
                                  midY
                                );
                                ctx.restore();
                              });
                            },
                          },
                        ]}
                      />
                    )}
                  </>
                )}

                {selectedFlexibility === "shoulder" && (
                  <>
                    {chartData?.length > 0 ? (
                      <Line
                        data={getFlexibilitySitChartData(
                          chartData,
                          period,
                          gender
                        )}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              min: flexibilityMin,
                              max: flexibilityMax,
                              ticks: {
                                stepSize: flexibilityStepSize,
                                color: "#ccc",
                                callback: (value) => `${value}`,
                              },
                            },
                            x: {
                              ticks: {
                                color: "#ccc",
                                autoSkip: period === "week",
                                maxRotation: 0,
                                minRotation: 0,
                                callback: function (value, index) {
                                  const label = this.getLabelForValue(value);
                                  const fullLabels = this.chart.data.labels;
                                  const firstLabel = fullLabels[0];
                                  const lastLabel =
                                    fullLabels[fullLabels.length - 1];

                                  if (period === "week") return label;
                                  if (
                                    label === firstLabel ||
                                    label === lastLabel
                                  )
                                    return label;
                                  return index % 5 === 0 ? label : "";
                                },
                              },
                              grid: { display: false },
                            },
                          },
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              backgroundColor: "#1c1c1e",
                              titleColor: "#fff",
                              bodyColor: "#ccc",
                              callbacks: {
                                title: (tooltipItems) =>
                                  `${tooltipItems[0].label}`,
                                label: (context) => {
                                  const value = context.parsed.y;
                                  if (value === 0) return t("notData");

                                  const thresholds =
                                    baseThresholdsShoulder[gender] || [];
                                  const zone = thresholds
                                    .slice()
                                    .reverse()
                                    .find((t) => value >= t.min);

                                  return `${value} cm — ${zone?.label || ""}`;
                                },
                              },
                            },
                          },
                        }}
                        plugins={[
                          {
                            id: "zoneLabelPlugin",
                            beforeDatasetsDraw(chart) {
                              const {
                                ctx,
                                chartArea: { top, bottom, left, right },
                                scales: { y },
                              } = chart;
                              if (!y) return;

                              const thresholds =
                                baseThresholdsShoulder[gender] || [];

                              thresholds.forEach((zone) => {
                                const from = Math.max(zone.min, y.min);
                                const to = Math.min(
                                  zone.max === Infinity ? y.max : zone.max,
                                  y.max
                                );

                                const yFrom = y.getPixelForValue(from);
                                const yTo = y.getPixelForValue(to);
                                const midY = (yFrom + yTo) / 2;

                                ctx.save();
                                ctx.fillStyle =
                                  getZoneColor(zone.label) || "#99999933";
                                ctx.fillRect(
                                  left,
                                  yTo,
                                  right - left,
                                  yFrom - yTo
                                );
                                ctx.restore();

                                ctx.save();
                                ctx.strokeStyle = "#00000033";
                                ctx.beginPath();
                                ctx.moveTo(left, yTo);
                                ctx.lineTo(right, yTo);
                                ctx.stroke();
                                ctx.restore();

                                ctx.save();
                                ctx.fillStyle = "#fff";
                                ctx.font = "bold 13px sans-serif";
                                ctx.textAlign = "center";
                                ctx.textBaseline = "middle";
                                ctx.fillText(
                                  zone.label,
                                  (left + right) / 2,
                                  midY
                                );
                                ctx.restore();
                              });
                            },
                          },
                        ]}
                      />
                    ) : (
                      <Line
                        data={getFlexibilitySitChartData(
                          chartData,
                          period,
                          gender
                        )}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              min: flexibilityMin || 0,
                              max: flexibilityMax || 100,
                              ticks: {
                                stepSize: flexibilityStepSize || 10,
                                color: "#ccc",
                                callback: (value) => `${value}`,
                              },
                              grid: { color: "#444" },
                            },
                            x: {
                              ticks: {
                                color: "#ccc",
                                autoSkip: period === "week",
                                maxRotation: 0,
                                minRotation: 0,
                                callback: function (value, index) {
                                  const label = this.getLabelForValue(value);
                                  const fullLabels = this.chart.data.labels;
                                  const firstLabel = fullLabels[0];
                                  const lastLabel =
                                    fullLabels[fullLabels.length - 1];

                                  if (period === "week") return label;
                                  if (
                                    label === firstLabel ||
                                    label === lastLabel
                                  )
                                    return label;
                                  return index % 5 === 0 ? label : "";
                                },
                              },
                              grid: { display: false },
                            },
                          },
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              backgroundColor: "#1c1c1e",
                              titleColor: "#fff",
                              bodyColor: "#ccc",
                              callbacks: {
                                title: (tooltipItems) =>
                                  `${tooltipItems[0].label}`,
                                label: (context) => {
                                  const value = context.parsed.y;
                                  if (value === 0) return t("notData");

                                  const thresholds =
                                    baseThresholdsShoulder[gender] || [];
                                  const zone = thresholds
                                    .slice()
                                    .reverse()
                                    .find((t) => value >= t.min);

                                  return `${value} cm — ${zone?.label || ""}`;
                                },
                              },
                            },
                          },
                        }}
                        plugins={[
                          {
                            id: "zoneLabelPlugin",
                            beforeDatasetsDraw(chart) {
                              const {
                                ctx,
                                chartArea: { top, bottom, left, right },
                                scales: { y },
                              } = chart;
                              if (!y) return;

                              const thresholds =
                                baseThresholdsShoulder[gender] || [];

                              thresholds.forEach((zone) => {
                                const from = Math.max(zone.min, y.min);
                                const to = Math.min(
                                  zone.max === Infinity ? y.max : zone.max,
                                  y.max
                                );

                                const yFrom = y.getPixelForValue(from);
                                const yTo = y.getPixelForValue(to);
                                const midY = (yFrom + yTo) / 2;

                                ctx.save();
                                ctx.fillStyle =
                                  getZoneColor(zone.label) || "#99999933";
                                ctx.fillRect(
                                  left,
                                  yTo,
                                  right - left,
                                  yFrom - yTo
                                );
                                ctx.restore();

                                ctx.save();
                                ctx.strokeStyle = "#00000033";
                                ctx.beginPath();
                                ctx.moveTo(left, yTo);
                                ctx.lineTo(right, yTo);
                                ctx.stroke();
                                ctx.restore();

                                ctx.save();
                                ctx.fillStyle = "#fff";
                                ctx.font = "bold 13px sans-serif";
                                ctx.textAlign = "center";
                                ctx.textBaseline = "middle";
                                ctx.fillText(
                                  zone.label,
                                  (left + right) / 2,
                                  midY
                                );
                                ctx.restore();
                              });
                            },
                          },
                        ]}
                      />
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {type === "Balance" && (
            <>
              <h5 className="text-white text-center mb-3">Balance Progress</h5>
              <div className="d-flex justify-content-between align-items-center">
                <div
                  className="ms-auto"
                  style={{ height: "100px", width: "200px" }}
                >
                  <Input
                    type="select"
                    options={CALCULATOR_DATA.CHARTDROPDOWN}
                    value={period}
                    onChange={handlePeriodChange}
                  />
                </div>
              </div>
              <Tabs
                tabs={CALCULATOR_DATA.BALENCE.map((option) => ({
                  label: <>{option.label}</>,
                  value: option.value,
                }))}
                active={CALCULATOR_DATA.BALENCE.findIndex(
                  (opt) => opt.value === selectedBalence
                )}
                setActive={handleBalenceTabChange}
              />

              <div style={{ height: "300px", width: "100%" }}>
                {selectedBalence === "singleleggedstand" && (
                  <Bar
                    data={getBalanceChartData(
                      chartData?.chartData || [],
                      period,
                      selectedBalence,
                      t
                    )}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          min: balMin,
                          max: balMax,
                          ticks: {
                            stepSize: balStepSize,
                            color: "#fff",
                          },
                          grid: { color: "#444" },
                        },
                        x: {
                          stacked: true,
                          offset: true,
                          ticks: {
                            color: "#fff",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") {
                                return label;
                              }
                              if (label === firstLabel || label === lastLabel) {
                                return label;
                              }
                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: {
                          labels: {
                            color: "#fff",
                            usePointStyle: false,
                            boxWidth: 10,
                            padding: 15,
                          },
                          position: "right",
                          align: "start",
                        },
                        tooltip: {
                          callbacks: {
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) return t("notData");
                              return `${value} steps - ${context.dataset.label}`;
                            },
                          },
                        },
                        datalabels: {
                          display: true,
                          color: "#fff",
                          anchor: "end",
                          align: "top",
                          formatter: (value, context) => {
                            if (
                              value > 0 &&
                              context.dataset.data.indexOf(
                                Math.max(...context.dataset.data)
                              ) === context.dataIndex
                            ) {
                              return `${value} steps - ${context.dataset.label}`;
                            }
                            return "";
                          },
                        },
                      },
                    }}
                  />
                )}

                {selectedBalence === "tandemwalk" && (
                  <Bar
                    data={getBalanceChartData(
                      chartData?.chartData || [],
                      period,
                      selectedBalence,
                      t
                    )}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          min: balMin,
                          max: balMax,
                          ticks: {
                            stepSize: balStepSize,
                            color: "#fff",
                          },
                          grid: { color: "#444" },
                        },
                        x: {
                          stacked: true,
                          offset: true,
                          ticks: {
                            color: "#fff",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") {
                                return label;
                              }
                              if (label === firstLabel || label === lastLabel) {
                                return label;
                              }
                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: {
                          labels: {
                            color: "#fff",
                            usePointStyle: false,
                            boxWidth: 10,
                            padding: 15,
                          },
                          position: "right",
                          align: "start",
                        },
                        tooltip: {
                          callbacks: {
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) return t("notData");
                              return `${value} steps - ${context.dataset.label}`;
                            },
                          },
                        },
                        datalabels: {
                          display: true,
                          color: "#fff",
                          anchor: "end",
                          align: "top",
                          formatter: (value, context) => {
                            if (
                              value > 0 &&
                              context.dataset.data.indexOf(
                                Math.max(...context.dataset.data)
                              ) === context.dataIndex
                            ) {
                              return `${value} steps - ${context.dataset.label}`;
                            }
                            return "";
                          },
                        },
                      },
                    }}
                  />
                )}
              </div>

              <div className="d-flex flex-column flex-md-row gap-2 mt-5 justify-content-center">
                {statuses.map((item, index) => (
                  <div
                    key={STATUS_KEYS[index]}
                    className="px-4 py-2 rounded-md text-sm font-semibold rounded-2"
                    style={{
                      backgroundColor:
                        index === activeIndex
                          ? "var(--primaryColor)"
                          : "#1c1c1e",
                      color: "white",
                    }}
                  >
                    {item}
                  </div>
                ))}
              </div>
            </>
          )}

          {type === "Strength" && (
            <>
              <h5 className="text-white text-center">STRENGTH PROGRESS</h5>
              <div className="d-flex justify-content-between align-items-center">
                <div
                  className="ms-auto"
                  style={{ height: "100px", width: "200px" }}
                >
                  <Input
                    type="select"
                    options={CALCULATOR_DATA.CHARTDROPDOWN}
                    value={period}
                    onChange={handlePeriodChange}
                  />
                </div>
              </div>
              <Tabs
                tabs={CALCULATOR_DATA.STRENGTH.map((option) => ({
                  label: <>{option.label}</>,
                  value: option.value,
                }))}
                active={CALCULATOR_DATA.STRENGTH.findIndex(
                  (opt) => opt.value === selectedStrength
                )}
                setActive={handleStrengthTabChange}
              />
              <div style={{ height: "300px", width: "100%" }}>
                {selectedStrength === "Push-ups" && (
                  <>
                    {chartData.length > 0 ? (
                      <Bar
                        data={getStrengthChartData(chartData, period, t)}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              min: strengthMin,
                              max: strengthMax,
                              ticks: {
                                stepSize: strengthStepSize,
                                color: "#fff",
                              },
                              grid: { color: "#444" },
                            },
                            x: {
                              stacked: true,
                              offset: true,
                              ticks: {
                                color: "#fff",
                                autoSkip: period === "week",
                                maxRotation: 0,
                                minRotation: 0,
                                callback: function (value, index) {
                                  const label = this.getLabelForValue(value);
                                  const fullLabels = this.chart.data.labels;
                                  const firstLabel = fullLabels[0];
                                  const lastLabel =
                                    fullLabels[fullLabels.length - 1];

                                  if (period === "week") {
                                    return label;
                                  }
                                  if (
                                    label === firstLabel ||
                                    label === lastLabel
                                  ) {
                                    return label;
                                  }
                                  return index % 5 === 0 ? label : "";
                                },
                              },
                              grid: { display: false },
                            },
                          },
                          plugins: {
                            legend: {
                              labels: {
                                color: "#fff",
                                usePointStyle: false,
                                boxWidth: 10,
                                padding: 15,
                              },
                              position: "right",
                              align: "start",
                            },
                            tooltip: {
                              callbacks: {
                                label: (context) => {
                                  const value = context.parsed.y;
                                  if (value === 0) return t("notData");
                                  return `${value} reps - ${context.dataset.label}`;
                                },
                              },
                            },
                            datalabels: {
                              display: true,
                              color: "#fff",
                              anchor: "end",
                              align: "top",
                              formatter: (value, context) => {
                                if (
                                  value > 0 &&
                                  context.dataset.data.indexOf(
                                    Math.max(...context.dataset.data)
                                  ) === context.dataIndex
                                ) {
                                  return `${value} reps - ${context.dataset.label}`;
                                }
                                return "";
                              },
                            },
                          },
                        }}
                      />
                    ) : (
                      <Bar
                        data={getStrengthChartData(chartData, period, t)}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              min: strengthMin,
                              max: strengthMax,
                              ticks: {
                                stepSize: strengthStepSize,
                                color: "#fff",
                              },
                              grid: { color: "#444" },
                            },
                            x: {
                              stacked: true,
                              offset: true,
                              ticks: {
                                color: "#fff",
                                autoSkip: period === "week",
                                maxRotation: 0,
                                minRotation: 0,
                                callback: function (value, index) {
                                  const label = this.getLabelForValue(value);
                                  const fullLabels = this.chart.data.labels;
                                  const firstLabel = fullLabels[0];
                                  const lastLabel =
                                    fullLabels[fullLabels.length - 1];

                                  if (period === "week") {
                                    return label;
                                  }
                                  if (
                                    label === firstLabel ||
                                    label === lastLabel
                                  ) {
                                    return label;
                                  }
                                  return index % 5 === 0 ? label : "";
                                },
                              },
                              grid: { display: false },
                            },
                          },
                          plugins: {
                            legend: {
                              labels: {
                                color: "#fff",
                                usePointStyle: false,
                                boxWidth: 10,
                                padding: 15,
                              },
                              position: "right",
                              align: "start",
                            },
                            tooltip: {
                              callbacks: {
                                label: (context) => {
                                  const value = context.parsed.y;
                                  if (value === 0) return t("notData");
                                  return `${value} reps - ${context.dataset.label}`;
                                },
                              },
                            },
                            datalabels: {
                              display: true,
                              color: "#fff",
                              anchor: "end",
                              align: "top",
                              formatter: (value, context) => {
                                if (
                                  value > 0 &&
                                  context.dataset.data.indexOf(
                                    Math.max(...context.dataset.data)
                                  ) === context.dataIndex
                                ) {
                                  return `${value} reps - ${context.dataset.label}`;
                                }
                                return "";
                              },
                            },
                          },
                        }}
                      />
                    )}
                  </>
                )}
                {selectedStrength === "Sit-ups" && (
                  <>
                    {chartData.length > 0 ? (
                      <Bar
                        data={getStrengthChartData(chartData, period, t)}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              min: strengthMin,
                              max: strengthMax,
                              ticks: {
                                stepSize: strengthStepSize,
                                color: "#fff",
                              },
                              grid: { color: "#444" },
                            },
                            x: {
                              stacked: true,
                              offset: true,
                              ticks: {
                                color: "#fff",
                                autoSkip: period === "week",
                                maxRotation: 0,
                                minRotation: 0,
                                callback: function (value, index) {
                                  const label = this.getLabelForValue(value);
                                  const fullLabels = this.chart.data.labels;
                                  const firstLabel = fullLabels[0];
                                  const lastLabel =
                                    fullLabels[fullLabels.length - 1];

                                  if (period === "week") {
                                    return label;
                                  }
                                  if (
                                    label === firstLabel ||
                                    label === lastLabel
                                  ) {
                                    return label;
                                  }
                                  return index % 5 === 0 ? label : "";
                                },
                              },
                              grid: { display: false },
                            },
                          },
                          plugins: {
                            legend: {
                              labels: {
                                color: "#fff",
                                usePointStyle: false,
                                boxWidth: 10,
                                padding: 15,
                              },
                              position: "right",
                              align: "start",
                            },
                            tooltip: {
                              callbacks: {
                                label: (context) => {
                                  const value = context.parsed.y;
                                  if (value === 0) return t("notData");
                                  return `${value} reps - ${context.dataset.label}`;
                                },
                              },
                            },
                            datalabels: {
                              display: true,
                              color: "#fff",
                              anchor: "end",
                              align: "top",
                              formatter: (value, context) => {
                                if (
                                  value > 0 &&
                                  context.dataset.data.indexOf(
                                    Math.max(...context.dataset.data)
                                  ) === context.dataIndex
                                ) {
                                  return `${value} reps - ${context.dataset.label}`;
                                }
                                return "";
                              },
                            },
                          },
                        }}
                      />
                    ) : (
                      <Bar
                        data={getStrengthChartData(chartData, period, t)}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              min: strengthMin,
                              max: strengthMax,
                              ticks: {
                                stepSize: strengthStepSize,
                                color: "#fff",
                              },
                              grid: { color: "#444" },
                            },
                            x: {
                              stacked: true,
                              offset: true,
                              ticks: {
                                color: "#fff",
                                autoSkip: period === "week",
                                maxRotation: 0,
                                minRotation: 0,
                                callback: function (value, index) {
                                  const label = this.getLabelForValue(value);
                                  const fullLabels = this.chart.data.labels;
                                  const firstLabel = fullLabels[0];
                                  const lastLabel =
                                    fullLabels[fullLabels.length - 1];

                                  if (period === "week") {
                                    return label;
                                  }
                                  if (
                                    label === firstLabel ||
                                    label === lastLabel
                                  ) {
                                    return label;
                                  }
                                  return index % 5 === 0 ? label : "";
                                },
                              },
                              grid: { display: false },
                            },
                          },
                          plugins: {
                            legend: {
                              labels: {
                                color: "#fff",
                                usePointStyle: false,
                                boxWidth: 10,
                                padding: 15,
                              },
                              position: "right",
                              align: "start",
                            },
                            tooltip: {
                              callbacks: {
                                label: (context) => {
                                  const value = context.parsed.y;
                                  if (value === 0) return t("notData");
                                  return `${value} reps - ${context.dataset.label}`;
                                },
                              },
                            },
                            datalabels: {
                              display: true,
                              color: "#fff",
                              anchor: "end",
                              align: "top",
                              formatter: (value, context) => {
                                if (
                                  value > 0 &&
                                  context.dataset.data.indexOf(
                                    Math.max(...context.dataset.data)
                                  ) === context.dataIndex
                                ) {
                                  return `${value} reps - ${context.dataset.label}`;
                                }
                                return "";
                              },
                            },
                          },
                        }}
                      />
                    )}
                  </>
                )}
                {selectedStrength === "Wall-Sit" && (
                  <>
                    {chartData.length > 0 ? (
                      <Bar
                        data={getStrengthChartData(chartData, period, t)}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              min: strengthMin,
                              max: strengthMax,
                              ticks: {
                                stepSize: strengthStepSize,
                                color: "#fff",
                              },
                              grid: { color: "#444" },
                            },
                            x: {
                              stacked: true,
                              offset: true,
                              ticks: {
                                color: "#fff",
                                autoSkip: period === "week",
                                maxRotation: 0,
                                minRotation: 0,
                                callback: function (value, index) {
                                  const label = this.getLabelForValue(value);
                                  const fullLabels = this.chart.data.labels;
                                  const firstLabel = fullLabels[0];
                                  const lastLabel =
                                    fullLabels[fullLabels.length - 1];

                                  if (period === "week") {
                                    return label;
                                  }
                                  if (
                                    label === firstLabel ||
                                    label === lastLabel
                                  ) {
                                    return label;
                                  }
                                  return index % 5 === 0 ? label : "";
                                },
                              },
                              grid: { display: false },
                            },
                          },
                          plugins: {
                            legend: {
                              labels: {
                                color: "#fff",
                                usePointStyle: false,
                                boxWidth: 10,
                                padding: 15,
                              },
                              position: "right",
                              align: "start",
                            },
                            tooltip: {
                              callbacks: {
                                label: (context) =>
                                  `${context.parsed.y} reps - ${context.dataset.label}`,
                              },
                            },
                            datalabels: {
                              display: true,
                              color: "#fff",
                              anchor: "end",
                              align: "top",
                              formatter: (value, context) => {
                                if (
                                  value > 0 &&
                                  context.dataset.data.indexOf(
                                    Math.max(...context.dataset.data)
                                  ) === context.dataIndex
                                ) {
                                  return `${value} reps - ${context.dataset.label}`;
                                }
                                return "";
                              },
                            },
                          },
                        }}
                      />
                    ) : (
                      <Bar
                        data={getStrengthChartData(chartData, period, t)}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              min: strengthMin,
                              max: strengthMax,
                              ticks: {
                                stepSize: strengthStepSize,
                                color: "#fff",
                              },
                              grid: { color: "#444" },
                            },
                            x: {
                              stacked: true,
                              offset: true,
                              ticks: {
                                color: "#fff",
                                autoSkip: period === "week",
                                maxRotation: 0,
                                minRotation: 0,
                                callback: function (value, index) {
                                  const label = this.getLabelForValue(value);
                                  const fullLabels = this.chart.data.labels;
                                  const firstLabel = fullLabels[0];
                                  const lastLabel =
                                    fullLabels[fullLabels.length - 1];

                                  if (period === "week") {
                                    return label;
                                  }
                                  if (
                                    label === firstLabel ||
                                    label === lastLabel
                                  ) {
                                    return label;
                                  }
                                  return index % 5 === 0 ? label : "";
                                },
                              },
                              grid: { display: false },
                            },
                          },
                          plugins: {
                            legend: {
                              labels: {
                                color: "#fff",
                                usePointStyle: false,
                                boxWidth: 10,
                                padding: 15,
                              },
                              position: "right",
                              align: "start",
                            },
                            tooltip: {
                              callbacks: {
                                label: (context) =>
                                  `${context.parsed.y} reps - ${context.dataset.label}`,
                              },
                            },
                            datalabels: {
                              display: true,
                              color: "#fff",
                              anchor: "end",
                              align: "top",
                              formatter: (value, context) => {
                                if (
                                  value > 0 &&
                                  context.dataset.data.indexOf(
                                    Math.max(...context.dataset.data)
                                  ) === context.dataIndex
                                ) {
                                  return `${value} reps - ${context.dataset.label}`;
                                }
                                return "";
                              },
                            },
                          },
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {type === "BMI" && (
            <>
              <h5 className="text-white text-center">BMI PROGRESS</h5>
              <div className="d-flex justify-content-between align-items-center">
                <div
                  className="ms-auto"
                  style={{ height: "100px", width: "200px" }}
                >
                  <Input
                    type="select"
                    options={CALCULATOR_DATA.CHARTDROPDOWN}
                    value={period}
                    onChange={handlePeriodChange}
                  />
                </div>
              </div>
              <div style={{ height: "300px", width: "100%" }}>
                {chartData?.length > 0 ? (
                  <Line
                    data={getBMIChartData(chartData, period)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          min: bmiMin,
                          max: bmiMax,
                          ticks: {
                            stepSize: bmiStepSize,
                            color: "#ccc",
                            callback: (value) => `${value}`,
                          },
                        },
                        x: {
                          ticks: {
                            color: "#ccc",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") return label;
                              if (label === firstLabel || label === lastLabel)
                                return label;
                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            title: (tooltipItems) => `${tooltipItems[0].label}`,
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) return t("notData");

                              const thresholds = baseBMIThresholds || [];
                              const zone = thresholds
                                .slice()
                                .reverse()
                                .find((t) => value >= t.min);

                              return `${value} — ${zone?.label || ""}`;
                            },
                          },
                        },
                      },
                    }}
                    plugins={[
                      {
                        id: "zoneLabelPlugin",
                        beforeDatasetsDraw(chart) {
                          const {
                            ctx,
                            chartArea: { top, bottom, left, right },
                            scales: { y },
                          } = chart;
                          if (!y) return;

                          const thresholds = baseBMIThresholds || [];

                          thresholds.forEach((zone) => {
                            const from = Math.max(zone.min, y.min);
                            const to = Math.min(
                              zone.max === Infinity ? y.max : zone.max,
                              y.max
                            );

                            const yFrom = y.getPixelForValue(from);
                            const yTo = y.getPixelForValue(to);
                            const midY = (yFrom + yTo) / 2;

                            ctx.save();
                            ctx.fillStyle =
                              getZoneColor(zone.label) || "#99999933";
                            ctx.fillRect(left, yTo, right - left, yFrom - yTo);
                            ctx.restore();

                            ctx.save();
                            ctx.strokeStyle = "#00000033";
                            ctx.beginPath();
                            ctx.moveTo(left, yTo);
                            ctx.lineTo(right, yTo);
                            ctx.stroke();
                            ctx.restore();

                            ctx.save();
                            ctx.fillStyle = "#fff";
                            ctx.font = "bold 13px sans-serif";
                            ctx.textAlign = "center";
                            ctx.textBaseline = "middle";
                            ctx.fillText(zone.label, (left + right) / 2, midY);
                            ctx.restore();
                          });
                        },
                      },
                    ]}
                  />
                ) : (
                  <Line
                    data={getBMIChartData(chartData, period)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          min: bmiMin,
                          max: bmiMax,
                          ticks: {
                            stepSize: bmiStepSize,
                            color: "#ccc",
                            callback: (value) => `${value}`,
                          },
                        },
                        x: {
                          ticks: {
                            color: "#ccc",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") return label;
                              if (label === firstLabel || label === lastLabel)
                                return label;
                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            title: (tooltipItems) => `${tooltipItems[0].label}`,
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) return t("notData");

                              const thresholds = baseBMIThresholds || [];
                              const zone = thresholds
                                .slice()
                                .reverse()
                                .find((t) => value >= t.min);

                              return `${value} — ${zone?.label || ""}`;
                            },
                          },
                        },
                      },
                    }}
                    plugins={[
                      {
                        id: "zoneLabelPlugin",
                        beforeDatasetsDraw(chart) {
                          const {
                            ctx,
                            chartArea: { top, bottom, left, right },
                            scales: { y },
                          } = chart;
                          if (!y) return;

                          const thresholds = baseBMIThresholds || [];

                          thresholds.forEach((zone) => {
                            const from = Math.max(zone.min, y.min);
                            const to = Math.min(
                              zone.max === Infinity ? y.max : zone.max,
                              y.max
                            );

                            const yFrom = y.getPixelForValue(from);
                            const yTo = y.getPixelForValue(to);
                            const midY = (yFrom + yTo) / 2;

                            ctx.save();
                            ctx.fillStyle =
                              getZoneColor(zone.label) || "#99999933";
                            ctx.fillRect(left, yTo, right - left, yFrom - yTo);
                            ctx.restore();

                            ctx.save();
                            ctx.strokeStyle = "#00000033";
                            ctx.beginPath();
                            ctx.moveTo(left, yTo);
                            ctx.lineTo(right, yTo);
                            ctx.stroke();
                            ctx.restore();

                            ctx.save();
                            ctx.fillStyle = "#fff";
                            ctx.font = "bold 13px sans-serif";
                            ctx.textAlign = "center";
                            ctx.textBaseline = "middle";
                            ctx.fillText(zone.label, (left + right) / 2, midY);
                            ctx.restore();
                          });
                        },
                      },
                    ]}
                  />
                )}
              </div>
            </>
          )}

          {type === "Body Fat" && (
            <>
              <h5 className="text-white text-center">BODY FAT PROGRESS</h5>
              <div className="d-flex justify-content-between align-items-center">
                <div
                  className="ms-auto"
                  style={{ height: "100px", width: "200px" }}
                >
                  <Input
                    type="select"
                    options={CALCULATOR_DATA.CHARTDROPDOWN}
                    value={period}
                    onChange={handlePeriodChange}
                  />
                </div>
              </div>
              <div style={{ height: "300px", width: "100%" }}>
                {chartData?.length > 0 ? (
                  <Line
                    data={getBFPChartData(chartData, period, gender)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          min: bfpMin,
                          max: bfpMax,
                          ticks: {
                            color: "#ccc",
                            stepSize: bfpStepSize,
                            autoSkip: false,
                            callback: (value) => `${value}%`,
                          },
                          grid: { color: "#333" },
                        },
                        x: {
                          ticks: {
                            color: "#ccc",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") {
                                return label;
                              }

                              if (label === firstLabel || label === lastLabel) {
                                return label;
                              }

                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            title: (tooltipItems) => {
                              const date = tooltipItems[0].label;
                              return `${date}`;
                            },
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) {
                                return t("notData");
                              }
                              return `${value}%`;
                            },
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <Line
                    data={getBFPChartData(chartData, period, gender)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          min: bfpMin,
                          max: bfpMax,
                          ticks: {
                            color: "#ccc",
                            stepSize: bfpStepSize,
                            autoSkip: false,
                            callback: (value) => `${value}%`,
                          },
                          grid: { color: "#333" },
                        },
                        x: {
                          ticks: {
                            color: "#ccc",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") {
                                return label;
                              }

                              if (label === firstLabel || label === lastLabel) {
                                return label;
                              }

                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            title: (tooltipItems) => {
                              const date = tooltipItems[0].label;
                              return `${date}`;
                            },
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) {
                                return t("notData");
                              }
                              return `${value}%`;
                            },
                          },
                        },
                      },
                    }}
                  />
                )}
              </div>
            </>
          )}

          {type === "BMR" && (
            <>
              <h5 className="text-white text-center">BMR PROGRESS</h5>
              <div className="d-flex justify-content-between align-items-center">
                <div
                  className="ms-auto"
                  style={{ height: "100px", width: "200px" }}
                >
                  <Input
                    type="select"
                    options={CALCULATOR_DATA.CHARTDROPDOWN}
                    value={period}
                    onChange={handlePeriodChange}
                  />
                </div>
              </div>
              <div
                style={{ height: "300px", width: "100%", minHeight: "300px" }}
              >
                {chartData?.length > 0 ? (
                  <Line
                    data={getBMRChartData(chartData, period)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          min,
                          max,
                          ticks: {
                            color: "#ccc",
                            stepSize,
                            autoSkip: false,
                            callback: (value) => `${value.toLocaleString()}`,
                          },
                          grid: { color: "#333" },
                        },
                        x: {
                          ticks: {
                            color: "#ccc",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") {
                                return label;
                              }

                              if (label === firstLabel || label === lastLabel) {
                                return label;
                              }

                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: {
                            display: false,
                          },
                        },
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            title: (tooltipItems) => {
                              const date = tooltipItems[0].label;
                              return `Date: ${date}`;
                            },
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) {
                                return t("notData");
                              }
                              return `BMR: ${value.toLocaleString()} kcal/day`;
                            },
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <Line
                    data={getBMRChartData(chartData, period)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          min,
                          max,
                          ticks: {
                            color: "#ccc",
                            stepSize,
                            autoSkip: false,
                            callback: (value) => `${value.toLocaleString()}`,
                          },
                          grid: { color: "#333" },
                        },
                        x: {
                          ticks: {
                            color: "#ccc",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") {
                                return label;
                              }

                              if (label === firstLabel || label === lastLabel) {
                                return label;
                              }

                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: {
                            display: false,
                          },
                        },
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            title: (tooltipItems) => {
                              const date = tooltipItems[0].label;
                              return `Date: ${date}`;
                            },
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) {
                                return t("notData");
                              }
                              return `BMR: ${value.toLocaleString()} kcal/day`;
                            },
                          },
                        },
                      },
                    }}
                  />
                )}
              </div>
            </>
          )}

          {type === "Macro" && (
            <>
              <h5 className="text-white text-center">
                MACRO PROGRESS OVER TIME
              </h5>
              <div className="d-flex justify-content-between align-items-center">
                <div
                  className="ms-auto"
                  style={{ height: "100px", width: "200px" }}
                >
                  <Input
                    type="select"
                    options={CALCULATOR_DATA.CHARTDROPDOWN}
                    value={period}
                    onChange={handlePeriodChange}
                  />
                </div>
              </div>
              <div style={{ height: "300px", width: "100%" }}>
                {chartData?.length > 0 ? (
                  <Bar
                    data={getMacroChartData(chartData, period, gender, t)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          min: macroMin,
                          max: macroMax,
                          stacked: true,
                          ticks: {
                            stepSize: macroStepSize,
                            color: "#fff",
                            callback: (value) => `${value}`,
                          },
                          grid: { color: "#444" },
                        },
                        x: {
                          stacked: true,
                          offset: true,
                          ticks: {
                            color: "#fff",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") {
                                return label;
                              }
                              if (label === firstLabel || label === lastLabel) {
                                return label;
                              }
                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: {
                          labels: {
                            color: "#fff",
                            usePointStyle: false,
                            boxWidth: 10,
                            padding: 15,
                          },
                          position: "right",
                          align: "start",
                        },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) return t("notData");
                              return `${value}g - ${context.dataset.label}`;
                            },
                          },
                        },
                        datalabels: {
                          display: true,
                          color: "#fff",
                          anchor: "end",
                          align: "top",
                          formatter: (value, context) => {
                            if (
                              value > 0 &&
                              context.dataset.data.indexOf(
                                Math.max(...context.dataset.data)
                              ) === context.dataIndex
                            ) {
                              return `${value}g - ${context.dataset.label}`;
                            }
                            return "";
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <Bar
                    data={getMacroChartData(chartData, period, gender, t)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          min: macroMin,
                          max: macroMax,
                          stacked: true,
                          ticks: {
                            stepSize: macroStepSize,
                            color: "#fff",
                            callback: (value) => `${value}`,
                          },
                          grid: { color: "#444" },
                        },
                        x: {
                          stacked: true,
                          offset: true,
                          ticks: {
                            color: "#fff",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") {
                                return label;
                              }
                              if (label === firstLabel || label === lastLabel) {
                                return label;
                              }
                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: {
                          labels: {
                            color: "#fff",
                            usePointStyle: false,
                            boxWidth: 10,
                            padding: 15,
                          },
                          position: "right",
                          align: "start",
                        },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) return t("notData");
                              return `${value}g - ${context.dataset.label}`;
                            },
                          },
                        },
                        datalabels: {
                          display: true,
                          color: "#fff",
                          anchor: "end",
                          align: "top",
                          formatter: (value, context) => {
                            if (
                              value > 0 &&
                              context.dataset.data.indexOf(
                                Math.max(...context.dataset.data)
                              ) === context.dataIndex
                            ) {
                              return `${value}g - ${context.dataset.label}`;
                            }
                            return "";
                          },
                        },
                      },
                    }}
                  />
                )}
              </div>
            </>
          )}

          {type === "HRC" && (
            <>
              <h5 className="text-white text-center">HEART RATE ZONE</h5>
              <div className="d-flex justify-content-between align-items-center">
                <div
                  className="ms-auto"
                  style={{ height: "100px", width: "200px" }}
                >
                  <Input
                    type="select"
                    options={CALCULATOR_DATA.CHARTDROPDOWN}
                    value={period}
                    onChange={handlePeriodChange}
                  />
                </div>
              </div>
              <div style={{ height: "300px", width: "100%" }}>
                {chartData?.length > 0 ? (
                  <Bar
                    data={getHRCChartData(chartData, period, t)}
                    options={{
                      indexAxis: "y",
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        x: {
                          min: 0,
                          max: 40,
                          ticks: {
                            color: "#ccc",
                            stepSize: 10,
                            callback: (value) => {
                              const percent = value + 50;
                              switch (value) {
                                case 0:
                                  return [`${percent}%`, t("zone1Note")];
                                case 10:
                                  return [`${percent}%`, t("zone2Note")];
                                case 20:
                                  return [`${percent}%`, t("zone3Note")];
                                case 30:
                                  return [`${percent}%`, t("zone4Note")];
                                case 40:
                                  return [`${percent}%`, "90%"];
                                default:
                                  return `${percent}%`;
                              }
                            },
                          },
                          stacked: true,
                          grid: {
                            drawTicks: false,
                            drawBorder: false,
                            color: "#444",
                          },
                        },
                        y: {
                          stacked: true,
                          ticks: { display: false },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          yAlign: "bottom",
                          position: "nearest",
                          displayColors: false,
                          callbacks: {
                            label: function (context) {
                              const dataset = getHRCChartData(
                                chartData,
                                period,
                                t
                              ).datasets[context.datasetIndex];
                              const activityKey = dataset.activityKey;

                              const matches = getHRCChartData(
                                chartData,
                                period,
                                t
                              )
                                .rawEntries.filter(
                                  (e) => e.activity === activityKey
                                )
                                .map((e) => {
                                  const date = dayjs(e.createdAt).format(
                                    "MMM D"
                                  );
                                  const activityLabel = dataset.label;
                                  return `${date} | ${e.result} bpm | ${activityLabel}`;
                                });

                              return matches.length > 0
                                ? matches
                                : "No data for this zone";
                            },
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <Bar
                    data={getHRCChartData(chartData, period, t)}
                    options={{
                      indexAxis: "y",
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        x: {
                          min: 0,
                          max: 40,
                          ticks: {
                            color: "#ccc",
                            stepSize: 10,
                            callback: (value) => {
                              const percent = value + 50;
                              switch (value) {
                                case 0:
                                  return [`${percent}%`, t("zone1Note")];
                                case 10:
                                  return [`${percent}%`, t("zone2Note")];
                                case 20:
                                  return [`${percent}%`, t("zone3Note")];
                                case 30:
                                  return [`${percent}%`, t("zone4Note")];
                                case 40:
                                  return [`${percent}%`, "90%"];
                                default:
                                  return `${percent}%`;
                              }
                            },
                          },
                          stacked: true,
                          grid: {
                            drawTicks: false,
                            drawBorder: false,
                            color: "#444",
                          },
                        },
                        y: {
                          stacked: true,
                          ticks: { display: false },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          yAlign: "bottom",
                          position: "nearest",
                          displayColors: false,
                          callbacks: {
                            label: function () {
                              return "No data for this zone";
                            },
                          },
                        },
                      },
                    }}
                  />
                )}
              </div>
            </>
          )}

          {type === "IBW" && (
            <>
              <h5 className="text-white text-center">IDEAL WEIGHT RANGE</h5>
              <div className="d-flex justify-content-between align-items-center">
                <div
                  className="ms-auto"
                  style={{ height: "100px", width: "200px" }}
                >
                  <Input
                    type="select"
                    options={CALCULATOR_DATA.CHARTDROPDOWN}
                    value={period}
                    onChange={handlePeriodChange}
                  />
                </div>
              </div>
              <div style={{ height: "300px", width: "100%" }}>
                {chartData?.length > 0 ? (
                  <Bar
                    data={getIBWChartData(chartData, period, gender)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          min: ibwMin,
                          max: ibwMax,
                          stacked: true,
                          ticks: {
                            stepSize: ibwStepSize,
                            color: "#fff",
                            callback: (value) => `${value} kg`,
                          },
                          grid: { color: "#444" },
                        },
                        x: {
                          stacked: true,
                          offset: true,
                          ticks: {
                            color: "#fff",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") {
                                return label;
                              }
                              if (label === firstLabel || label === lastLabel) {
                                return label;
                              }
                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: {
                          labels: {
                            color: "#fff",
                            usePointStyle: false,
                            boxWidth: 10,
                            padding: 15,
                          },
                          position: "right",
                          align: "start",
                        },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) return t("notData");
                              return `${value} kg - ${context.dataset.label}`;
                            },
                          },
                        },
                        datalabels: {
                          display: true,
                          color: "#fff",
                          anchor: "end",
                          align: "top",
                          formatter: (value, context) => {
                            if (
                              value > 0 &&
                              context.dataset.data.indexOf(
                                Math.max(...context.dataset.data)
                              ) === context.dataIndex
                            ) {
                              return `${value} kg`;
                            }
                            return "";
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <Bar
                    data={getIBWChartData(chartData, period, gender)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          min: ibwMin,
                          max: ibwMax,
                          stacked: true,
                          ticks: {
                            stepSize: ibwStepSize,
                            color: "#fff",
                            callback: (value) => `${value} kg`,
                          },
                          grid: { color: "#444" },
                        },
                        x: {
                          stacked: true,
                          offset: true,
                          ticks: {
                            color: "#fff",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") {
                                return label;
                              }
                              if (label === firstLabel || label === lastLabel) {
                                return label;
                              }
                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: {
                          labels: {
                            color: "#fff",
                            usePointStyle: false,
                            boxWidth: 10,
                            padding: 15,
                          },
                          position: "right",
                          align: "start",
                        },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) return t("notData");
                              return `${value} kg - ${context.dataset.label}`;
                            },
                          },
                        },
                        datalabels: {
                          display: true,
                          color: "#fff",
                          anchor: "end",
                          align: "top",
                          formatter: (value, context) => {
                            if (
                              value > 0 &&
                              context.dataset.data.indexOf(
                                Math.max(...context.dataset.data)
                              ) === context.dataIndex
                            ) {
                              return `${value} kg`;
                            }
                            return "";
                          },
                        },
                      },
                    }}
                  />
                )}
              </div>
            </>
          )}

          {type === "Sleep" && (
            <>
              <h5 className="text-white text-center mb-3">RECOMMENDED SLEEP</h5>

              <div className="d-flex justify-content-between align-items-center">
                <div
                  className="ms-auto"
                  style={{ height: "100px", width: "200px" }}
                >
                  <Input
                    type="select"
                    options={CALCULATOR_DATA.CHARTDROPDOWN}
                    value={period}
                    onChange={handlePeriodChange}
                  />
                </div>
              </div>

              {(() => {
                let labels = [];
                if (period === "week") {
                  labels = getLastNDaysLabels(7);
                } else if (period === "month") {
                  const today = dayjs();
                  const start = today.subtract(30, "day");
                  let curr = start.startOf("day");
                  while (curr.isBefore(today) || curr.isSame(today)) {
                    labels.push(curr.format("MMM D"));
                    curr = curr.add(1, "day");
                  }
                }

                const scoreMap = {};
                chartData.forEach((d) => {
                  scoreMap[formatToMonthDay(d.date)] = {
                    score: d.score,
                    status: d.status,
                  };
                });

                const remapScore = (score) => {
                  if (!score) return 0;
                  return score - 6;
                };

                const scores = labels.map((lbl) =>
                  scoreMap[lbl]?.score ? remapScore(scoreMap[lbl].score) : 0
                );

                const pointColors = labels.map((lbl) => {
                  const rawStatus = scoreMap[lbl]?.status;

                  const status =
                    typeof rawStatus === "string"
                      ? rawStatus.toLowerCase().replace(/\s+/g, "")
                      : "";

                  if (!status) return "transparent";
                  if (status === "lightRecovery") return "var(--yellowColor)";
                  if (status === "balanced") return "var(--greenColor)";
                  if (status === "deepRecovery") return "var(--primaryColor)";

                  return "#6c63ff";
                });

                const statusMap = {
                  "light recovery": "lightRecovery",
                  balanced: "balanced",
                  "balanced recovery": "balanced",
                  "deep recovery": "deepRecovery",
                };

                return (
                  <div style={{ height: "300px", width: "100%" }}>
                    <Line
                      data={{
                        labels,
                        datasets: [
                          {
                            label: "Sleep Score (hrs)",
                            data: scores,
                            borderColor: "#6c63ff",
                            borderWidth: 2,
                            tension: 0.3,
                            pointRadius: 6,
                            pointBackgroundColor: pointColors,
                            pointBorderColor: pointColors,
                          },
                        ],
                      }}
                      options={{
                        maintainAspectRatio: false,
                        responsive: true,
                        plugins: {
                          tooltip: {
                            backgroundColor: "#1c1c1e",
                            titleColor: "#fff",
                            bodyColor: "#ccc",
                            callbacks: {
                              title: (tooltipItems) => {
                                const date = tooltipItems[0].label;
                                return `${date}`;
                              },
                              label: (ctx) => {
                                const lbl = labels[ctx.dataIndex];
                                const point = scoreMap[lbl];
                                if (point) {
                                  const raw = point.status;
                                  const normalized =
                                    typeof raw === "string"
                                      ? raw.toLowerCase()
                                      : raw;

                                  const key = statusMap[normalized] || raw;

                                  return `${point.score} hrs (${t(key)})`;
                                }
                                return t("notData");
                              },
                            },
                          },
                          legend: { display: false },
                        },
                        scales: {
                          y: {
                            min: 0,
                            max: 4,
                            ticks: {
                              stepSize: 0.5,
                              color: "#ccc",
                              callback: (value) => {
                                if (value === 0) return "0";
                                if (value === 1) return "7h";
                                if (value === 2) return "8h";
                                if (value === 3) return "9h";
                                if (value === 4) return "10h";
                                return "";
                              },
                            },
                            grid: { color: "#333" },
                          },
                          x: {
                            ticks: {
                              color: "#ccc",
                              autoSkip: false,
                              maxRotation: 0,
                              minRotation: 0,
                              callback: function (value, index) {
                                const label = this.getLabelForValue(value);
                                const fullLabels = this.chart.data.labels;
                                const firstLabel = fullLabels[0];
                                const lastLabel =
                                  fullLabels[fullLabels.length - 1];

                                if (period === "week") {
                                  return label;
                                }

                                if (
                                  label === firstLabel ||
                                  label === lastLabel
                                ) {
                                  return label;
                                }

                                return index % 5 === 0 ? label : "";
                              },
                              grid: { display: false },
                            },
                          },
                        },
                      }}
                    />
                  </div>
                );
              })()}

              <div className="d-flex justify-content-center gap-3 mt-5 text-dark">
                <span
                  className="p-2 rounded-2"
                  style={{
                    backgroundColor: "var(--yellowColor)",
                    color: "#fff",
                  }}
                >
                  {t("lightRecovery")} (7–7.5 hrs)
                </span>
                <span
                  className="p-2 rounded-2"
                  style={{
                    backgroundColor: "var(--greenColor)",
                    color: "#fff",
                  }}
                >
                  {t("balanced")} (7.5–9 hrs)
                </span>
                <span
                  className="p-2 rounded-2"
                  style={{
                    backgroundColor: "var(--primaryColor)",
                    color: "#fff",
                  }}
                >
                  {t("deepRecovery")} (9–10 hrs)
                </span>
              </div>
            </>
          )}

          {type === "ORM" && (
            <>
              <h5 className="text-white text-center">ONE-REP MAX PROGRESS</h5>
              <div className="d-flex justify-content-between align-items-center">
                <div
                  className="ms-auto"
                  style={{ height: "100px", width: "200px" }}
                >
                  <Input
                    type="select"
                    options={CALCULATOR_DATA.CHARTDROPDOWN}
                    value={period}
                    onChange={handlePeriodChange}
                  />
                </div>
              </div>
              <div style={{ height: "300px", width: "100%" }}>
                {chartData?.length > 0 ? (
                  <Line
                    data={getORMChartData(chartData, period)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          min: ormMin,
                          max: ormMax,
                          ticks: {
                            color: "#ccc",
                            stepSize: ormStepSize,
                            callback: (value) => `${value}`,
                          },
                          grid: { color: "#333" },
                        },
                        x: {
                          ticks: {
                            color: "#ccc",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") {
                                return label;
                              }

                              if (label === firstLabel || label === lastLabel) {
                                return label;
                              }

                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: {
                          labels: {
                            color: "#fff",
                            usePointStyle: true,
                            boxWidth: 10,
                            padding: 15,
                          },
                          position: "top",
                        },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            title: (tooltipItems) => {
                              const value = tooltipItems[0].parsed.y;
                              if (value === 0) return "";
                              return tooltipItems[0].dataset.label;
                            },
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) return t("notData");
                              return `${value} kg on ${context.label}`;
                            },
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <Line
                    data={getORMChartData(chartData, period)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          min: ormMin,
                          max: ormMax,
                          ticks: {
                            color: "#ccc",
                            stepSize: ormStepSize,
                            callback: (value) => `${value}`,
                          },
                          grid: { color: "#333" },
                        },
                        x: {
                          ticks: {
                            color: "#ccc",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") {
                                return label;
                              }

                              if (label === firstLabel || label === lastLabel) {
                                return label;
                              }

                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: {
                          labels: {
                            color: "#fff",
                            usePointStyle: true,
                            boxWidth: 10,
                            padding: 15,
                          },
                          position: "top",
                        },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            title: (tooltipItems) => {
                              const value = tooltipItems[0].parsed.y;
                              if (value === 0) return "";
                              return tooltipItems[0].dataset.label;
                            },
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) return t("notData");
                              return `${value} kg on ${context.label}`;
                            },
                          },
                        },
                      },
                    }}
                  />
                )}
              </div>
            </>
          )}

          {type === "Hydration" && (
            <>
              <h5 className="text-white text-center">HYDRATION PROGRESS</h5>
              <div className="d-flex justify-content-between align-items-center">
                <div
                  className="ms-auto"
                  style={{ height: "100px", width: "200px" }}
                >
                  <Input
                    type="select"
                    options={CALCULATOR_DATA.CHARTDROPDOWN}
                    value={period}
                    onChange={handlePeriodChange}
                  />
                </div>
              </div>
              <div style={{ height: "300px", width: "100%" }}>
                {chartData?.length > 0 ? (
                  <Line
                    data={getHydrationChartData(chartData, period)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          min: hydMin,
                          max: hydMax,
                          ticks: {
                            color: "#ccc",
                            stepSize: hydStepSize,
                            autoSkip: false,
                            callback: (value) => `${value} L`,
                          },
                          grid: { color: "#333" },
                        },
                        x: {
                          ticks: {
                            color: "#ccc",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") {
                                return label;
                              }

                              if (label === firstLabel || label === lastLabel) {
                                return label;
                              }

                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            title: (tooltipItems) => {
                              const date = tooltipItems[0].label;
                              return `${date}`;
                            },
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) {
                                return t("notData");
                              }
                              return `${value} L`;
                            },
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <Line
                    data={getHydrationChartData(chartData, period)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          min: hydMin,
                          max: hydMax,
                          ticks: {
                            color: "#ccc",
                            stepSize: hydStepSize,
                            autoSkip: false,
                            callback: (value) => `${value} L`,
                          },
                          grid: { color: "#333" },
                        },
                        x: {
                          ticks: {
                            color: "#ccc",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") {
                                return label;
                              }

                              if (label === firstLabel || label === lastLabel) {
                                return label;
                              }

                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            title: (tooltipItems) => {
                              const date = tooltipItems[0].label;
                              return `${date}`;
                            },
                            label: (context) => {
                              const value = context.parsed.y;
                              if (value === 0) {
                                return t("notData");
                              }
                              return `${value} L`;
                            },
                          },
                        },
                      },
                    }}
                  />
                )}
              </div>
            </>
          )}

          {type === "WHR" && (
            <>
              <h5 className="text-white text-center">WHR PROGRESS</h5>
              <div className="d-flex justify-content-between align-items-center">
                <div
                  className="ms-auto"
                  style={{ height: "100px", width: "200px" }}
                >
                  <Input
                    type="select"
                    options={CALCULATOR_DATA.CHARTDROPDOWN}
                    value={period}
                    onChange={handlePeriodChange}
                  />
                </div>
              </div>
              <div style={{ height: "300px", width: "100%" }}>
                {chartData?.length > 0 ? (
                  <Line
                    data={getWHRChartData(chartData, period, gender)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          min: whrMin,
                          max: whrMax,
                          ticks: {
                            color: "#ccc",
                            stepSize: whrStepSize,
                            callback: (value) => Number(value).toFixed(2),
                          },
                        },
                        x: {
                          ticks: {
                            color: "#ccc",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") return label;
                              if (label === firstLabel || label === lastLabel)
                                return label;
                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            title: (tooltipItems) => `${tooltipItems[0].label}`,
                            label: (context) => {
                              const value = context.parsed.y;
                              return value === 0 ? t("notData") : `${value}`;
                            },
                          },
                        },
                      },
                    }}
                    plugins={[
                      {
                        id: "zoneLabelPlugin",
                        beforeDatasetsDraw(chart) {
                          const {
                            ctx,
                            chartArea: { top, bottom, left, right },
                            scales: { y },
                          } = chart;

                          if (!y) return;

                          const zones =
                            gender === "male"
                              ? [
                                  {
                                    label: t("riskLevels.high"),
                                    from: 1.0,
                                    to: y.max,
                                    color: "#b91c1c",
                                  },
                                  {
                                    label: t("riskLevels.moderate"),
                                    from: 0.9,
                                    to: 1.0,
                                    color: "#d97706",
                                  },
                                  {
                                    label: t("riskLevels.low"),
                                    from: 0.0,
                                    to: 0.9,
                                    color: "#15803d",
                                  },
                                ]
                              : [
                                  {
                                    label: t("riskLevels.high"),
                                    from: 0.85,
                                    to: y.max,
                                    color: "#b91c1c",
                                  },
                                  {
                                    label: t("riskLevels.moderate"),
                                    from: 0.8,
                                    to: 0.85,
                                    color: "#d97706",
                                  },
                                  {
                                    label: t("riskLevels.low"),
                                    from: 0.0,
                                    to: 0.8,
                                    color: "#15803d",
                                  },
                                ];

                          zones.forEach((zone) => {
                            const from = Math.max(zone.from, y.min);
                            const to = Math.min(zone.to, y.max);
                            const yFrom = y.getPixelForValue(from);
                            const yTo = y.getPixelForValue(to);
                            const midY = (yFrom + yTo) / 2;

                            ctx.save();
                            ctx.fillStyle = zone.color;
                            ctx.globalAlpha = 0.25;
                            ctx.fillRect(left, yTo, right - left, yFrom - yTo);
                            ctx.restore();

                            ctx.save();
                            ctx.strokeStyle = "#00000033";
                            ctx.beginPath();
                            ctx.moveTo(left, yTo);
                            ctx.lineTo(right, yTo);
                            ctx.stroke();
                            ctx.restore();

                            ctx.save();
                            ctx.fillStyle = "#fff";
                            ctx.font = "bold 13px sans-serif";
                            ctx.textAlign = "center";
                            ctx.textBaseline = "middle";
                            ctx.fillText(zone.label, (left + right) / 2, midY);
                            ctx.restore();
                          });
                        },
                      },
                    ]}
                  />
                ) : (
                  <Line
                    data={getWHRChartData(chartData, period, gender)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          min: whrMin,
                          max: whrMax,
                          ticks: {
                            color: "#ccc",
                            stepSize: whrStepSize,
                            callback: (value) => Number(value).toFixed(2),
                          },
                        },
                        x: {
                          ticks: {
                            color: "#ccc",
                            autoSkip: period === "week",
                            maxRotation: 0,
                            minRotation: 0,
                            callback: function (value, index) {
                              const label = this.getLabelForValue(value);
                              const fullLabels = this.chart.data.labels;
                              const firstLabel = fullLabels[0];
                              const lastLabel =
                                fullLabels[fullLabels.length - 1];

                              if (period === "week") return label;
                              if (label === firstLabel || label === lastLabel)
                                return label;
                              return index % 5 === 0 ? label : "";
                            },
                          },
                          grid: { display: false },
                        },
                      },
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: "#1c1c1e",
                          titleColor: "#fff",
                          bodyColor: "#ccc",
                          callbacks: {
                            title: (tooltipItems) => `${tooltipItems[0].label}`,
                            label: (context) => {
                              const value = context.parsed.y;
                              return value === 0 ? t("notData") : `${value}`;
                            },
                          },
                        },
                      },
                    }}
                    plugins={[
                      {
                        id: "zoneLabelPlugin",
                        beforeDatasetsDraw(chart) {
                          const {
                            ctx,
                            chartArea: { top, bottom, left, right },
                            scales: { y },
                          } = chart;

                          if (!y) return;

                          const zones =
                            gender === "male"
                              ? [
                                  {
                                    label: t("riskLevels.high"),
                                    from: 1.0,
                                    to: y.max,
                                    color: "#b91c1c",
                                  },
                                  {
                                    label: t("riskLevels.moderate"),
                                    from: 0.9,
                                    to: 1.0,
                                    color: "#d97706",
                                  },
                                  {
                                    label: t("riskLevels.low"),
                                    from: 0.0,
                                    to: 0.9,
                                    color: "#15803d",
                                  },
                                ]
                              : [
                                  {
                                    label: t("riskLevels.high"),
                                    from: 0.85,
                                    to: y.max,
                                    color: "#b91c1c",
                                  },
                                  {
                                    label: t("riskLevels.moderate"),
                                    from: 0.8,
                                    to: 0.85,
                                    color: "#d97706",
                                  },
                                  {
                                    label: t("riskLevels.low"),
                                    from: 0.0,
                                    to: 0.8,
                                    color: "#15803d",
                                  },
                                ];

                          zones.forEach((zone) => {
                            const from = Math.max(zone.from, y.min);
                            const to = Math.min(zone.to, y.max);
                            const yFrom = y.getPixelForValue(from);
                            const yTo = y.getPixelForValue(to);
                            const midY = (yFrom + yTo) / 2;

                            ctx.save();
                            ctx.fillStyle = zone.color;
                            ctx.globalAlpha = 0.25;
                            ctx.fillRect(left, yTo, right - left, yFrom - yTo);
                            ctx.restore();

                            ctx.save();
                            ctx.strokeStyle = "#00000033";
                            ctx.beginPath();
                            ctx.moveTo(left, yTo);
                            ctx.lineTo(right, yTo);
                            ctx.stroke();
                            ctx.restore();

                            ctx.save();
                            ctx.fillStyle = "#fff";
                            ctx.font = "bold 13px sans-serif";
                            ctx.textAlign = "center";
                            ctx.textBaseline = "middle";
                            ctx.fillText(zone.label, (left + right) / 2, midY);
                            ctx.restore();
                          });
                        },
                      },
                    ]}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FitnessToolsResultCard;
